//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { ISubscription } from '@account-settings/redux/emailReports/emailReportsModel';
import { mapStringToTopic } from '@account-settings/redux/emailReports/emailReportsUtil';
import {
  IGetSubscriptionResponse,
 } from '@apis/account-settings/emailReportsApiModel';
import { serializedGatewayFetch } from '@utils/sessionFetch';

const BASE_URL = '/api/v1/subscriptions';

function emailReportsFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  return serializedGatewayFetch('user_messaging_gateway', `${BASE_URL}${endpoint}`, init);
}

/**
 * Get the list of the client's subscriptions
 *
 * GET /api/v1/subscriptions
 */
export async function getClientSubscriptions(): Promise<ISubscription[]> {
  const res = await emailReportsFetch('');
  const result = await res.json() as IGetSubscriptionResponse;
  return result.data.subscriptions.map((subscription) => {
    return {
      clientId: subscription.clientId,
      userId: subscription.userId,
      userEmail: subscription.userEmail,
      topic: mapStringToTopic(subscription.topic),
      output: subscription.output,
      config: JSON.parse(subscription.config),
      // If we get a lastSent time back, create a new date for it
      // we need to multiply by 1000 because JS dates are in milliseconds and we're getting back seconds
      lastSent: subscription.lastSent ? new Date(subscription.lastSent * 1000) : undefined,
      version: subscription.version,
    };
  });
}

/**
 * Create a list of subscriptions for the client
 *
 * subscriptions - List of subscriptions to be added/updated
 *
 * PUT /api/v1/subscriptions
 */
export async function addSubscriptions(subscriptions: ISubscription[]): Promise<void> {
  await emailReportsFetch('', {
    method: 'PUT',
    body: JSON.stringify({
      subscriptions: subscriptions.map((subscription) => {
        return {
          clientId: subscription.clientId,
          userId: subscription.userId,
          userEmail: subscription.userEmail,
          topic: subscription.topic,
          output: subscription.output,
          config: JSON.stringify(subscription.config),
          lastSent: subscription.lastSent,
          version: subscription.version,
        };
      }),
    }),
  });
}

/**
 * update a subscription for the client
 *
 * subscription - Subscription to be updated
 *
 * PUT /api/v1/subscriptions/:userId/:topic/:output
 */
export async function updateSubscription(subscription: ISubscription): Promise<void> {
  await emailReportsFetch(`/${subscription.userId}/${subscription.topic}/${subscription.output}`, {
    method: 'PUT',
    body: JSON.stringify({
      subscription: {
        clientId: subscription.clientId,
        userId: subscription.userId,
        userEmail: subscription.userEmail,
        topic: subscription.topic,
        output: subscription.output,
        config: JSON.stringify(subscription.config),
        lastSent: subscription.lastSent,
        version: subscription.version,
      },
    }),
  });
}

/**
 * Delete a subscription for the client
 *
 * DELETE /api/v1/subscriptions/:userId/:topic/:output
 */
export async function deleteSubscription(subscription: ISubscription): Promise<void> {
  await emailReportsFetch(`/${subscription.userId}/${subscription.topic}/${subscription.output}`, {
    method: 'DELETE'
  });
}
